import { Component, Input, OnInit } from '@angular/core';
import { SchedulerOptionsComponent } from '../scheduler-options-component';
import { CronSchedulerDefinition, Frequency, SchedulerDefinition } from '../../../../api/scheduler-service';
import { isValidCron } from 'cron-validator';
import cronstrue from 'cronstrue';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';

const DEFAULT_CRON = '0 */15 * * * *';

/** Macros accepted by Spring's CronExpression but not by `cron-validator`. */
const SPRING_MACROS = new Set([
    '@yearly', '@annually', '@monthly', '@weekly', '@daily', '@midnight', '@hourly',
]);

/** Common one-click presets. The second column is the cron expression that
 *  feeds straight into `cronControl`. */
export const CRON_PRESETS: Array<{ label: string; expr: string; }> = [
    { label: 'Every 15 minutes',                  expr: '0 */15 * * * *' },
    { label: 'Every 30 minutes',                  expr: '0 */30 * * * *' },
    { label: 'Every hour',                        expr: '0 0 * * * *' },
    { label: 'Every weekday at 09:00',            expr: '0 0 9 * * MON-FRI' },
    { label: 'Every day at midnight',             expr: '@daily' },
    { label: 'Every Monday at 09:00',             expr: '0 0 9 * * MON' },
    { label: 'First day of month at 09:00',       expr: '0 0 9 1 * *' },
    { label: 'Last day of month at 23:59',        expr: '0 59 23 L * *' },
    { label: 'First Monday of month at 09:00',    expr: '0 0 9 ? * MON#1' },
];

/** Builder modes — translate the user's plain-language intent into a cron
 *  expression. Each mode renders its own small input panel; switching modes
 *  recomputes the expression on the next field-touch. */
type BuilderMode = 'EVERY_N_MINUTES'
                 | 'EVERY_N_HOURS'
                 | 'DAILY_AT'
                 | 'WEEKLY_AT'
                 | 'MONTHLY_AT_DAY'
                 | 'MONTHLY_NTH_WEEKDAY';

const WEEKDAYS_FULL = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

@Component({
    selector: 'cron-scheduler-options',
    imports: [
        MatExpansionModule, MatDividerModule, MatSlideToggleModule,
        MatButtonToggleModule, MatSelectModule, MatIconModule,
        MatChipsModule, MatFormFieldModule, MatTooltipModule,
        CommonModule, MatInputModule, ReactiveFormsModule, FormsModule,
    ],
    templateUrl: './cron-scheduler-options.component.html',
    styleUrl: './cron-scheduler-options.component.css',
})
export class CronSchedulerOptionsComponent implements SchedulerOptionsComponent, OnInit {

    @Input() valueChange: Function | undefined;

    isValid: boolean = true;
    previewText: string = '';

    /** The single source of truth — both presets and builder write here. The
     *  free-text input (Expert mode) edits it directly. */
    cronControl: FormControl = new FormControl(DEFAULT_CRON);
    dependency: FormControl = new FormControl(false);

    /** UI tab — 'PRESETS' for one-click, 'BUILDER' for plain-language inputs,
     *  'EXPERT' for raw expression editing. Default to PRESETS so first-time
     *  users see a friendly entry point. */
    activeMode: 'PRESETS' | 'BUILDER' | 'EXPERT' = 'PRESETS';

    presets = CRON_PRESETS;

    // ── Builder field controls ───────────────────────────────────────────────

    builderMode: FormControl<BuilderMode> = new FormControl('DAILY_AT' as BuilderMode, { nonNullable: true });

    /** EVERY_N_MINUTES / EVERY_N_HOURS — the N value. */
    everyN: FormControl<number> = new FormControl(15, { nonNullable: true });

    /** DAILY_AT / WEEKLY_AT / MONTHLY_AT_DAY / MONTHLY_NTH_WEEKDAY — the time of day. */
    atHour:   FormControl<number> = new FormControl(9,  { nonNullable: true });
    atMinute: FormControl<number> = new FormControl(0,  { nonNullable: true });

    /** WEEKLY_AT — single weekday from MON–SUN. */
    weeklyDay: FormControl<string> = new FormControl('MON', { nonNullable: true });

    /** DAILY_AT — optional weekday filter. Empty array = all days. */
    dailyWeekdays: FormControl<string[]> = new FormControl<string[]>([], { nonNullable: true });

    /** MONTHLY_AT_DAY — day-of-month (1..31, or 'L' for last). */
    monthDay: FormControl<string> = new FormControl('1', { nonNullable: true });

    /** MONTHLY_NTH_WEEKDAY — Nth occurrence of a weekday (e.g. 1st Monday). */
    nthWeekday: FormControl<string> = new FormControl('MON', { nonNullable: true });
    nthOccurrence: FormControl<string> = new FormControl('1',   { nonNullable: true }); // '1'..'4' or 'L'

    weekdays = WEEKDAYS_FULL;
    hours    = Array.from({ length: 24 }, (_, i) => i);
    minutes  = Array.from({ length: 60 }, (_, i) => i);
    daysOfMonth = Array.from({ length: 31 }, (_, i) => String(i + 1)).concat('L');

    cronOptionsForm: FormGroup = new FormGroup({
        cron: this.cronControl,
        dependency: this.dependency,
    });

    ngOnInit(): void {
        this.recomputePreview(this.cronControl.value);
        this.cronControl.valueChanges.subscribe(value => this.recomputePreview(value));

        // Builder field changes recompose the expression. Subscribed per-control
        // so TS doesn't have to merge the union-typed FormControl signatures.
        this.builderMode.valueChanges.subscribe(()    => this.composeFromBuilder());
        this.everyN.valueChanges.subscribe(()         => this.composeFromBuilder());
        this.atHour.valueChanges.subscribe(()         => this.composeFromBuilder());
        this.atMinute.valueChanges.subscribe(()       => this.composeFromBuilder());
        this.weeklyDay.valueChanges.subscribe(()      => this.composeFromBuilder());
        this.dailyWeekdays.valueChanges.subscribe(()  => this.composeFromBuilder());
        this.monthDay.valueChanges.subscribe(()       => this.composeFromBuilder());
        this.nthWeekday.valueChanges.subscribe(()     => this.composeFromBuilder());
        this.nthOccurrence.valueChanges.subscribe(()  => this.composeFromBuilder());

        setTimeout(() => this.cronOptionsForm.valueChanges.subscribe(() => {
            if (this.valueChange != undefined) {
                this.valueChange();
            }
        }), 0);
    }

    setPreset(expr: string): void {
        this.cronControl.setValue(expr);
    }

    /** Build a 6-field cron from the builder mode's inputs. Power users who
     *  want non-trivial multi-field combinations switch to EXPERT mode. */
    private composeFromBuilder(): void {
        const mode = this.builderMode.value;
        const hh = this.atHour.value;
        const mm = this.atMinute.value;
        let expr = '';
        switch (mode) {
            case 'EVERY_N_MINUTES':
                expr = `0 */${this.clampN(this.everyN.value, 1, 59)} * * * *`;
                break;
            case 'EVERY_N_HOURS':
                expr = `0 0 */${this.clampN(this.everyN.value, 1, 23)} * * *`;
                break;
            case 'DAILY_AT': {
                const dows = this.dailyWeekdays.value;
                const dowField = (dows && dows.length > 0) ? dows.join(',') : '*';
                expr = `0 ${mm} ${hh} * * ${dowField}`;
                break;
            }
            case 'WEEKLY_AT':
                expr = `0 ${mm} ${hh} * * ${this.weeklyDay.value}`;
                break;
            case 'MONTHLY_AT_DAY':
                expr = `0 ${mm} ${hh} ${this.monthDay.value} * *`;
                break;
            case 'MONTHLY_NTH_WEEKDAY': {
                const occ = this.nthOccurrence.value;
                // Spring CronExpression: <dow>L for last, <dow>#<n> for nth
                const dowField = occ === 'L' ? `${this.nthWeekday.value}L` : `${this.nthWeekday.value}#${occ}`;
                expr = `0 ${mm} ${hh} ? * ${dowField}`;
                break;
            }
        }
        if (expr) {
            this.cronControl.setValue(expr, { emitEvent: true });
        }
    }

    private clampN(v: number, lo: number, hi: number): number {
        if (v == null || isNaN(v)) return lo;
        return Math.min(hi, Math.max(lo, v));
    }

    /** Validate against the same 6-field shape Spring's CronExpression accepts +
     *  the macro set both libraries support. Update isValid + previewText so the
     *  template can render a live status. */
    private recomputePreview(value: string | null | undefined): void {
        const expr = (value ?? '').trim();
        if (!expr) {
            this.isValid = false;
            this.previewText = '';
            return;
        }

        if (SPRING_MACROS.has(expr.toLowerCase())) {
            this.isValid = true;
            try {
                this.previewText = cronstrue.toString(expr);
            } catch {
                this.previewText = expr;
            }
            return;
        }

        this.isValid = isValidCron(expr, { seconds: true, alias: true });
        if (!this.isValid) {
            this.previewText = '';
            return;
        }

        try {
            this.previewText = cronstrue.toString(expr);
        } catch {
            this.previewText = '';
        }
    }

    getSchedulerDefinition(): SchedulerDefinition {
        const sd: CronSchedulerDefinition = {
            frequency: Frequency.Cron,
            cronExpression: this.cronControl.value,
            eventDependency: this.dependency.value,
        };
        return sd;
    }
}
